import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { ethers } from "ethers";

import { AccountStateEnum } from "../../contexts/AuthContext";
import { ConnectWalletContext } from "../../contexts/ConnectWalletContext";
import { getWalletProviderKind } from "../../config/chains";
import { supportedSigningChainIds } from "../../utils/wallet/connectors";
import { SDK_KIND_TO_WALLET_ENUM } from "../../utils/wallet/types";
import { IConnectWalletModalProps, ConnectWalletPageEnum } from "./types";
import { useModalSetup } from "./useModalSetup";
import { useModalPaging } from "./useModalPaging";

type Ctx = IConnectWalletModalProps &
  ReturnType<typeof useModalSetup> &
  ReturnType<typeof useModalPaging>;

export function useModalEffects(ctx: Ctx) {
  const {
    onHide,
    setPageError,
    setDepositError,
    setApprovedAmountAndSignature,
    setYieldVaultToggle,
    selectedDepositChain,
    chainId,
    setSelectedDepositChain,
    approvalSpender,
    getAssetPermitSignature,
    assetAllowance,
    getAllowance,
    setAssetAllowance,
    setLoadingAllowance,
    showInvalidRefModal,
    directDepositFlow,
    referralCode,
    yieldVaultToggle,
    account,
    isWrongNetwork,
    accountSigningKeyState,
    accountApiKeyState,
    inviteRequired,
    availablePages,
    setAvailablePages,
    setLoading,
    accountData,
    isAccountImported,
    setCurrentPageIndex,
    updatePageIndex,
    showOnboarding,
    pageIndex,
    connectedWallet,
    setWallet,
    show,
    setWalletAndConnect,
    depositAmount,
    approvedAmountAndSignature,
    useUnifiedVaultFlow,
    isNativeDepositAsset,
    decimals,
    isUnifiedVaultApproved,
  } = ctx;

  const resetStates = useCallback(() => {
    setPageError("");
    setDepositError("");
    setApprovedAmountAndSignature({
      approvedAmount: undefined,
      signature: undefined,
      deadline: 0,
    });
  }, []);

  const onHideClicked = useCallback(() => {
    resetStates();
    setYieldVaultToggle(false);
    onHide?.();
  }, [onHide, resetStates, setYieldVaultToggle]);

  // When network is changed, reset states
  useEffect(() => {
    resetStates();
  }, [selectedDepositChain, resetStates]);

  // When chain is switched, set selected deposit chain
  useEffect(() => {
    if (chainId && supportedSigningChainIds.includes(chainId)) {
      setSelectedDepositChain(chainId);
    }
  }, [chainId]);

  // When asset is changed, get allowance if needed
  useEffect(() => {
    if (approvalSpender && (!getAssetPermitSignature || !assetAllowance)) {
      const loadAssetAllowance = async () => {
        setLoadingAllowance(true);
        try {
          const allowance = await getAllowance(approvalSpender);
          setAssetAllowance(allowance);
        } catch (error) {
          // Nothing
        } finally {
          setLoadingAllowance(false);
        }
      };
      loadAssetAllowance();
    } else {
      setAssetAllowance("0");
    }
  }, [approvalSpender, assetAllowance, getAllowance, getAssetPermitSignature]);

  useEffect(() => {
    if (showInvalidRefModal) {
      onHideClicked();
    }
  }, [onHideClicked, showInvalidRefModal]);

  useEffect(() => {
    // On show, decide which page to show
    // If wallet already connected, onboarding and connect wallet no longer available
    const pages: ConnectWalletPageEnum[] = [];

    if (!directDepositFlow && (referralCode || yieldVaultToggle)) {
      pages.push(ConnectWalletPageEnum.ONBOARD);
    }

    if (!directDepositFlow || !account) {
      pages.push(ConnectWalletPageEnum.CONNECT_WALLET);
    }

    if (isWrongNetwork) {
      pages.push(ConnectWalletPageEnum.SWITCH_NETWORK);
    }

    if (
      accountSigningKeyState === AccountStateEnum.REQUIRE_REGISTER_SIGNING ||
      accountApiKeyState === AccountStateEnum.REQUIRE_REGISTER_SIGNING ||
      inviteRequired
    ) {
      pages.push(ConnectWalletPageEnum.ENABLE_TRADING_SIGN);
    }

    pages.push(ConnectWalletPageEnum.DEPOSIT);

    if (JSON.stringify(availablePages) !== JSON.stringify(pages)) {
      setAvailablePages(pages);
    }

    setLoading(false);
  }, [
    account,
    accountApiKeyState,
    accountData?.equity,
    accountSigningKeyState,
    availablePages,
    inviteRequired,
    isAccountImported,
    isWrongNetwork,
    referralCode,
    yieldVaultToggle,
    directDepositFlow,
  ]);

  useEffect(() => {
    if (!account) {
      updatePageIndex(0);
    } else if (isWrongNetwork) {
      setCurrentPageIndex(ConnectWalletPageEnum.SWITCH_NETWORK);
    } else if (
      accountSigningKeyState === AccountStateEnum.REQUIRE_REGISTER_SIGNING ||
      accountApiKeyState === AccountStateEnum.REQUIRE_REGISTER_SIGNING ||
      inviteRequired
    ) {
      setCurrentPageIndex(ConnectWalletPageEnum.ENABLE_TRADING_SIGN);
    } else {
      if (
        showOnboarding !== undefined &&
        !showOnboarding &&
        !yieldVaultToggle &&
        !directDepositFlow
      ) {
        onHide?.();
      }
      setCurrentPageIndex(ConnectWalletPageEnum.DEPOSIT);
    }
  }, [
    account,
    accountData?.equity,
    accountSigningKeyState,
    inviteRequired,
    isWrongNetwork,
    onHide,
    updatePageIndex,
    yieldVaultToggle,
    directDepositFlow,
    showOnboarding,
    setCurrentPageIndex,
    accountApiKeyState,
  ]);

  // When the page changes, stop loading
  useEffect(() => {
    setLoading(false);
  }, [pageIndex]);

  useEffect(() => {
    if (connectedWallet) setWallet(connectedWallet);
  }, [connectedWallet]);

  const hasValidSignature = useMemo(() => {
    if (
      depositAmount &&
      depositAmount === approvedAmountAndSignature.approvedAmount &&
      approvedAmountAndSignature.signature
    ) {
      return true;
    }
    return false;
  }, [
    approvedAmountAndSignature.approvedAmount,
    approvedAmountAndSignature.signature,
    depositAmount,
  ]);

  const [unifiedVaultApprovalChecked, setUnifiedVaultApprovalChecked] =
    useState(false);

  // Check UnifiedVault approval status
  useEffect(() => {
    if (!useUnifiedVaultFlow || !depositAmount) {
      setUnifiedVaultApprovalChecked(false);
      return;
    }

    if (isNativeDepositAsset) {
      setUnifiedVaultApprovalChecked(true);
      return;
    }

    if (useUnifiedVaultFlow && depositAmount) {
      const checkApproval = async () => {
        try {
          const amountWei = ethers.utils
            .parseUnits(depositAmount, decimals)
            .toString();
          const approved = await isUnifiedVaultApproved(amountWei);
          setUnifiedVaultApprovalChecked(approved);
        } catch (error) {
          setUnifiedVaultApprovalChecked(false);
        }
      };
      checkApproval();
    }
  }, [
    useUnifiedVaultFlow,
    depositAmount,
    isNativeDepositAsset,
    decimals,
    isUnifiedVaultApproved,
  ]);

  const depositReady = useMemo(() => {
    if (chainId !== selectedDepositChain) {
      return false;
    }

    // For UnifiedVault flow, native deposits do not need ERC20 approval.
    if (useUnifiedVaultFlow) {
      return isNativeDepositAsset || unifiedVaultApprovalChecked;
    }

    // IF permit is allowed, check for valid signature.
    // ELSE, check for allowance
    if (getAssetPermitSignature) {
      return hasValidSignature;
    }
    return Number(assetAllowance) >= (Number(depositAmount) || 0);
  }, [
    chainId,
    selectedDepositChain,
    useUnifiedVaultFlow,
    isNativeDepositAsset,
    unifiedVaultApprovalChecked,
    getAssetPermitSignature,
    assetAllowance,
    depositAmount,
    hasValidSignature,
  ]);

  const page = useMemo(
    () => availablePages[pageIndex],
    [availablePages, pageIndex]
  );

  // Direct-open for SDK chains: when the connect step is reached, immediately fire
  // the SDK connect so its own native modal opens — the user never sees our
  // intermediate "Select Your Wallet" picker (there's only one option anyway).
  const sdkWalletEnum = SDK_KIND_TO_WALLET_ENUM[getWalletProviderKind()];
  const { connectIntent } = useContext(ConnectWalletContext);
  const lastSdkConnectIntentRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (
      !show ||
      page !== ConnectWalletPageEnum.CONNECT_WALLET ||
      account ||
      sdkWalletEnum === undefined
    ) {
      return;
    }
    // `connectIntent` changes on every Connect Wallet click, including when
    // `show` is already true (Para closed, our flow not yet torn down). Without
    // that, auto-connect latches once and the header button looks dead.
    if (lastSdkConnectIntentRef.current === connectIntent) return;
    lastSdkConnectIntentRef.current = connectIntent;
    setWalletAndConnect(sdkWalletEnum);
  }, [
    show,
    page,
    account,
    sdkWalletEnum,
    setWalletAndConnect,
    connectIntent,
  ]);

  return {
    resetStates,
    onHideClicked,
    hasValidSignature,
    unifiedVaultApprovalChecked,
    setUnifiedVaultApprovalChecked,
    depositReady,
    page,
  };
}
